import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, LogOut, Copy, BarChart2, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import toast from "react-hot-toast";
import { usersAPI } from "../api";

const TelegramLinkButton: React.FC = () => {
  const { updateUser } = useAuth();

  useEffect(() => {
    const el = document.getElementById("tg-link-btn");
    if (!el) return;
    el.innerHTML = "";

    (window as any).onTelegramLink = async (tgUser: any) => {
      try {
        await usersAPI.linkTelegram(tgUser);
        updateUser({ telegram_username: tgUser.username || "linked" });
        toast.success("Telegram ulandi!");
      } catch (e: any) {
        toast.error(e?.response?.data?.message || "Xato");
      }
    };

    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", "prava_test_uz_bot");
    script.setAttribute("data-size", "large");
    script.setAttribute("data-userpic", "false");
    script.setAttribute("data-onauth", "onTelegramLink(user)");
    el.appendChild(script);

    return () => {
      el.innerHTML = "";
      delete (window as any).onTelegramLink;
    };
  }, [updateUser]);

  return (
    <div
      id="tg-link-btn"
      style={{ display: "flex", justifyContent: "center" }}
    />
  );
};

// ─── Settings Page ──────────────────────────────────────────────
const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const [notif, setNotif] = useState(true);
  const [copied, setCopied] = useState(false);
  const [shuffleQuestions, setShuffleQuestions] = useState(
    () => localStorage.getItem("shuffle_questions") === "true",
  );
  const [shuffleAnswers, setShuffleAnswers] = useState(
    () => localStorage.getItem("shuffle_answers") === "true",
  );

  const toggleShuffleQuestions = () => {
    const next = !shuffleQuestions;
    setShuffleQuestions(next);
    localStorage.setItem("shuffle_questions", String(next));
  };
  const toggleShuffleAnswers = () => {
    const next = !shuffleAnswers;
    setShuffleAnswers(next);
    localStorage.setItem("shuffle_answers", String(next));
  };

  const langs = [
    { code: "oz", label: "O'zbekcha (lotin)" },
    { code: "uz", label: "Ўзбекча (кирилл)" },
    { code: "ru", label: "Русский" },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(user?.unique_code || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCode = (code: string | null | undefined) => {
    if (!code) return "---";
    return `${code.slice(0, 3)}-${code.slice(3, 6)}-${code.slice(6)}`;
  };

  const Section = ({ title, children }: any) => (
    <div
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 14,
        padding: 20,
        marginBottom: 12,
      }}>
      <p
        style={{
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 16,
        }}>
        {title}
      </p>
      {children}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {/* HEADER */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: 52,
        }}>
        <div
          style={{
            maxWidth: 520,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
            }}>
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            {t("settings")}
          </span>
        </div>
      </div>

      {/* CONTENT */}
      <div
        style={{ maxWidth: 520, margin: "0 auto", padding: "68px 16px 32px" }}>
        {/* PROFILE */}
        <Section title={t("profile")}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 16,
            }}>
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                background: "#3B82F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 800,
                fontSize: 20,
              }}>
              {user?.fullName?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div>
              <p style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                {user?.fullName}
              </p>
              <p style={{ color: "var(--muted)", fontSize: 12 }}>
                {user?.email}
              </p>
            </div>
          </div>

          {[
            ["fullName", user?.fullName || ""],
            ["email", user?.email || ""],
          ].map(([key, val]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: "block",
                  color: "var(--muted)",
                  fontSize: 11,
                  fontWeight: 700,
                  marginBottom: 5,
                }}>
                {t(key)}
              </label>
              <input
                defaultValue={val}
                style={{
                  width: "100%",
                  background: "#0D1526",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  color: "#E2E8F0",
                  fontSize: 14,
                  outline: "none",
                }}
              />
            </div>
          ))}

          <button
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              border: "none",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}>
            {t("save")}
          </button>
        </Section>

        {/* STATISTIKA */}
        <button
          onClick={() => navigate("/statistics")}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 14,
            padding: 16,
            marginBottom: 12,
            cursor: "pointer",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BarChart2 size={18} color="#3B82F6" />
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
              {t("statistics")}
            </span>
          </div>
          <ChevronRight size={16} color="var(--muted)" />
        </button>

        {/* UNIQUE CODE */}
        <Section title="Mening kodim">
          <p style={{ color: "var(--muted)", fontSize: 12, marginBottom: 10 }}>
            Ustoz shu kodni so'raydi — unga yuboring
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "#0D1526",
              border: "1px solid var(--border)",
              borderRadius: 10,
              padding: "10px 14px",
            }}>
            <span
              style={{
                flex: 1,
                color: "#60A5FA",
                fontWeight: 800,
                fontSize: 20,
                letterSpacing: 4,
                fontFamily: "monospace",
              }}>
              {formatCode(user?.unique_code)}
            </span>
            <button
              onClick={handleCopy}
              style={{
                background:
                  copied ? "rgba(34,197,94,.15)" : "rgba(59,130,246,.15)",
                border: "none",
                borderRadius: 8,
                padding: "6px 12px",
                cursor: "pointer",
                color: copied ? "#22C55E" : "#3B82F6",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 4,
                transition: "all .2s",
              }}>
              {copied ?
                <>
                  <Check size={12} /> Nusxalandi
                </>
              : <>
                  <Copy size={12} /> Nusxalash
                </>
              }
            </button>
          </div>
        </Section>

        {/* TELEGRAM ULASH — faqat telegram_username yo'q bo'lsa */}
        {!user?.telegram_username && (
          <Section title="Telegram ulash">
            <p
              style={{ color: "var(--muted)", fontSize: 12, marginBottom: 10 }}>
              Telegram akkauntingizni ulab, bir nechta qurilmadan kiring
            </p>
            <TelegramLinkButton />
          </Section>
        )}

        {/* Telegram ulangan bo'lsa ko'rsat */}
        {user?.telegram_username && (
          <Section title="Telegram">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>✈️</span>
              <div>
                <p style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
                  @{user.telegram_username}
                </p>
                <p style={{ color: "#22C55E", fontSize: 12 }}>Ulangan ✓</p>
              </div>
            </div>
          </Section>
        )}

        {/* LANGUAGE */}
        <Section title={t("language")}>
          {langs.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code as any)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "11px 14px",
                borderRadius: 10,
                border: `1.5px solid ${lang === l.code ? "#3B82F6" : "var(--border)"}`,
                background:
                  lang === l.code ? "rgba(59,130,246,.1)" : "transparent",
                marginBottom: 8,
                cursor: "pointer",
              }}>
              <span
                style={{
                  color: lang === l.code ? "#60A5FA" : "var(--muted)",
                  fontWeight: 600,
                  fontSize: 13,
                }}>
                {l.label}
              </span>
              {lang === l.code && (
                <Check size={14} style={{ color: "#3B82F6" }} />
              )}
            </button>
          ))}
        </Section>

        {/* QUIZ SOZLAMALARI */}
        <Section title="Test sozlamalari">
          {[
            {
              label: "Savollarni aralash",
              desc: "Har testda savollar tartibsiz chiqadi",
              value: shuffleQuestions,
              toggle: toggleShuffleQuestions,
            },
            {
              label: "Javoblarni aralash",
              desc: "Har savolda variantlar tartibsiz chiqadi",
              value: shuffleAnswers,
              toggle: toggleShuffleAnswers,
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
              }}>
              <div>
                <p style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>
                  {item.label}
                </p>
                <p style={{ color: "var(--muted)", fontSize: 11, marginTop: 2 }}>
                  {item.desc}
                </p>
              </div>
              <button
                onClick={item.toggle}
                style={{
                  width: 42,
                  height: 24,
                  borderRadius: 99,
                  background: item.value ? "#3B82F6" : "var(--border)",
                  border: "none",
                  cursor: "pointer",
                  position: "relative",
                  flexShrink: 0,
                  marginLeft: 12,
                }}>
                <div
                  style={{
                    position: "absolute",
                    top: 3,
                    left: item.value ? 18 : 3,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "#fff",
                    transition: "left .2s",
                  }}
                />
              </button>
            </div>
          ))}
        </Section>

        {/* NOTIFICATIONS */}
        <Section title={t("notifications")}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            <p style={{ color: "var(--muted)", fontSize: 13 }}>
              {t("dailyReminder")}
            </p>
            <button
              onClick={() => setNotif(!notif)}
              style={{
                width: 42,
                height: 24,
                borderRadius: 99,
                background: notif ? "#3B82F6" : "var(--border)",
                border: "none",
                cursor: "pointer",
                position: "relative",
              }}>
              <div
                style={{
                  position: "absolute",
                  top: 3,
                  left: notif ? 18 : 3,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left .2s",
                }}
              />
            </button>
          </div>
        </Section>

        {/* LOGOUT */}
        <button
          onClick={() => {
            logout();
            navigate("/auth");
          }}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "13px",
            borderRadius: 12,
            border: "1px solid rgba(239,68,68,.3)",
            background: "rgba(239,68,68,.08)",
            color: "#EF4444",
            fontWeight: 700,
            fontSize: 14,
            cursor: "pointer",
          }}>
          <LogOut size={16} /> {t("logout")}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
